import {
  CLEAR_FLIGHT_RESULT,
  CLEAR_FLIGHT_SEARCH_RESULT,
  UPDATE_FLIGHT_FILTERS,
  UPDATE_FLIGHTS_SORT,
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  FETCH_POPULAR_AIRPORTS,
  SET_FLIGHTS_CREDIT_DATA,
  FETCH_AIRLINES,
  FETCH_FLIGHT_CREDIT_RESERVATION,
  FETCH_FLIGHT_DEALS,
  FETCH_FLIGHT_DEAL,
  FETCH_FLIGHT_CAMPAIGNS,
  FETCH_FLIGHT_CAMPAIGN_DEALS,
  FETCH_FLIGHT_CAMPAIGN,
  UPDATE_FLIGHT_TRAVELLERS,
  UPDATE_FLIGHT_SEAT_SELECTIONS,
  CLEAR_FLIGHT_SEAT_SELECTIONS,
  FLIGHTS_DATE_CHANGE_SET_STEP,
  FLIGHTS_DATE_CHANGE_SELECTED_LEGS_TO_CHANGE,
  FLIGHTS_DATE_CHANGE_NEW_LEGS_WITH_DATES,
  FLIGHTS_DATE_CHANGE_RESET,
  CHECKOUT_INITIALISE,
  CHECKOUT_PROCESS_SUCCESS,
} from 'actions/actionConstants'
import { createReducer, reducerSwitch } from 'lib/redux/reducerUtils'
import { arrayToObject, sortBy } from 'lib/array/arrayUtils'
import { FETCH_FLIGHT_JOURNEY, FETCH_FLIGHTS, FETCH_FLIGHT_PRICE, FETCH_FLIGHT_CALENDAR, FETCH_SEARCH_FLIGHTS, FETCH_FLIGHT_FARE_FAMILIES, FETCH_FLIGHT_SEATS, FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST } from 'actions/apiActionConstants'
import { ApiAction } from 'middlewares/apiMiddleware'
import { FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'

// One empty flightsDateChange, shared by every place that needs to start over.
const EMPTY_FLIGHTS_DATE_CHANGE: App.FlightsState['flightsDateChange'] = {
  currentStep: FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE,
  selectedLegsToChange: [],
  selectedLegWithNewDepartureDate: [],
}

const initialState: App.FlightsState = {
  // Old booking fields, do not use
  journeys: [],
  filters: [],
  flightSearchV2: {
    onewayFares: [],
    searchId: undefined,
  },
  sort: 'fastest',
  isFetching: false,
  isFetched: false,
  hasError: false,
  fetchedKey: '',
  errorMessage: '',
  // Actual fields
  journeysById: {},
  isBaggageFetching: false,
  isBaggageFetchingError: false,
  flightPricesLoading: {},
  flightPrices: {},
  fareFamiliesByFlight: {},
  flightCalendarLoading: {},
  flightCalendar: {},
  searchFlights: {},
  searchV2Flights: {},
  journeyFetching: {},
  journeyErrors: {},
  popularAirports: {
    origins: [],
    destinations: [],
    fetching: false,
  },
  creditDetails: undefined,
  creditRebookingReservation: undefined,
  isCreditReservationFetching: false,
  isCreditReservationError: false,
  flightDeals: {},
  flightDealLists: {},
  notices: [],
  campaigns: {},
  campaign: {},
  campaignDeals: {},
  airlines: {},
  isUpdatingTravellers: false,
  hasUpdatedTravellers: false,
  seatsMap: {},
  seatSelections: {},
  flightsDateChangeSearch: {},
  flightsDateChange: EMPTY_FLIGHTS_DATE_CHANGE,
}

const apiRequests = reducerSwitch<App.FlightsState>({
  [FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST]: (state, action) => ({
    flightsDateChangeSearch: {
      ...state.flightsDateChangeSearch,
      [action.key]: {
        fares: [],
        fareIdsToUpsellFarePerLeg: {},
        isFetching: true,
      },
    },
  }),
  [FETCH_AIRLINES]: (state, action) => ({
    airlines: { ...state.airlines, [action.key]: { data: undefined, fetching: true } },
  }),
  [FETCH_FLIGHT_JOURNEY]: (state, action) => ({
    journeyFetching: { ...state.journeyFetching, [action.key]: true },
  }),
  [FETCH_FLIGHTS]: (state, action) => ({
    journeys: [],
    fetchedKey: action.key,
    isFetching: true,
    isFetched: false,
  }),
  [FETCH_SEARCH_FLIGHTS]: (state, action) => ({
    searchFlights: {
      ...state.searchFlights,
      [action.key]: {
        journeys: [],
        fetching: true,
      },
    },
    searchV2Flights: {
      ...state.searchV2Flights,
      [action.key]: {
        fares: [],
        fetching: true,
      },
    },
  }),
  [FETCH_FLIGHT_CALENDAR]: (state, action) => ({
    flightCalendarLoading: {
      ...state.flightCalendarLoading,
      [action.key]: true,
    },
  }),
  [FETCH_FLIGHT_PRICE]: (state, action) => ({
    flightPricesLoading: {
      ...state.flightPricesLoading,
      [action.key]: true,
    },
  }),
  [FETCH_FLIGHT_FARE_FAMILIES]: (state, action) => ({
    fareFamiliesByFlight: {
      ...state.fareFamiliesByFlight,
      [action.key]: {
        fetching: true,
        families: [],
        familiesPerLeg: [],
      },
    },
  }),
  [FETCH_FLIGHT_CREDIT_RESERVATION]: () => ({
    isCreditReservationFetching: true,
    creditRebookingReservation: undefined,
    isCreditReservationError: false,
  }),
  [FETCH_FLIGHT_DEALS]: (state, action) => ({
    flightDealLists: {
      ...state.flightDealLists,
      [action.key]: {
        dealIds: [],
        error: undefined,
        fetching: true,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGNS]: (state, action) => ({
    campaigns: {
      ...state.campaigns,
      [action.key]: {
        data: [],
        error: undefined,
        fetching: true,
        isEnabled: false,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGN]: (state, action) => ({
    campaign: {
      ...state.campaign,
      [action.key]: {
        data: undefined,
        error: undefined,
        fetching: true,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGN_DEALS]: (state, action) => ({
    campaignDeals: {
      ...state.campaignDeals,
      [action.key]: {
        data: [],
        error: undefined,
        fetching: true,
      },
    },
  }),
  [FETCH_FLIGHT_DEAL]: (state, action) => ({
    flightDeals: {
      ...state.flightDeals,
      [action.id]: {
        error: undefined,
        fetching: true,
      },
    },
  }),
  [FETCH_POPULAR_AIRPORTS]: (state) => ({
    popularAirports: {
      ...state.popularAirports,
      fetching: true,
    },
  }),
  [UPDATE_FLIGHT_TRAVELLERS]: () => ({
    isUpdatingTravellers: true,
    hasUpdatedTravellers: false,
  }),
  [FETCH_FLIGHT_SEATS]: (state, action) => ({
    seatsMap: {
      ...state.seatsMap,
      [action.key]: {
        fetching: true,
        data: undefined,
      },
    },
  }),
})

const apiSuccesses = reducerSwitch<App.FlightsState>({
  [FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST]: (state, action) => ({
    flightsDateChangeSearch: {
      ...state.flightsDateChangeSearch,
      [action.key]: {
        fares: action.data.onewayFares,
        fareIdsToUpsellFarePerLeg: action.data.fareIdsToUpsellFarePerLeg,
        searchId: action.data.searchId,
        isFetching: false,
      },
    },
  }),
  [FETCH_AIRLINES]: (state, action: ApiAction<Array<App.Airline>>) => {
    // The GET airlines endpoint is also filtering the airline by names, when
    // the code given, so in order to not fetch again for every single airline
    // we are transforming the response array into object and merging it with
    // the state.
    const fetchedAirlines = arrayToObject(action.data, (airline) => airline.airlineCode)
    const airlines = { ...state.airlines }

    Object.entries(fetchedAirlines).forEach(([key, value]) => {
      airlines[key] = { data: value, fetching: false }
    })

    return { airlines }
  },
  [FETCH_FLIGHT_JOURNEY]: (state, action) => ({
    journeysById: {
      ...state.journeysById,
      [action.key]: action.data,
    },
    journeyFetching: { ...state.journeyFetching, [action.key]: false },
  }),
  [FETCH_FLIGHTS]: (state, action) => ({
    isFetching: false,
    isFetched: true,
    journeys: 'journeys' in action.data ? action.data.journeys : [],
    ...('onewayFares' in action.data && {
      flightSearchV2: {
        onewayFares: action.data.onewayFares,
        searchId: action.data.searchId,
      },
    }),
    notices: action.data.notices,
    hasError: false,
    errorMessage: '',
  }),
  [FETCH_SEARCH_FLIGHTS]: (state, action) => {
    if (action.data.onewayFares) {
      return {
        searchFlights: {
          ...state.searchFlights,
          [action.key]: {
            ...state.searchFlights[action.key],
            fetching: false,
          },
        },
        searchV2Flights: {
          ...state.searchV2Flights,
          [action.key]: {
            fares: action.data.onewayFares,
            fetching: false,
            searchId: action.data.searchId,
            sessionId: action.data.sessionId,
            viewType: action.data.viewType,
            metadata: action.data.metadata,
            timestamp: action.data.timestamp,
          },
        },
      }
    } else {
      return {
        searchFlights: {
          ...state.searchFlights,
          [action.key]: {
            journeys: action.data.journeys,
            viewType: action.data.viewType,
            fetching: false,
          },
        },
        searchV2Flights: {
          ...state.searchV2Flights,
          [action.key]: {
            ...state.searchV2Flights[action.key],
            fetching: false,
          },
        },
        notices: action.data.notices,
        sort: action.data.sort,
      }
    }
  },
  [FETCH_FLIGHT_PRICE]: (state, action) => ({
    flightPrices: {
      ...state.flightPrices,
      [action.key]: action.data,
    },
    flightPricesLoading: {
      ...state.flightPricesLoading,
      [action.key]: false,
    },
  }),
  [FETCH_FLIGHT_FARE_FAMILIES]: (state, action) => ({
    fareFamiliesByFlight: {
      ...state.fareFamiliesByFlight,
      [action.key]: {
        fetching: false,
        families: action.data.upsellFares,
        familiesPerLeg: action.data.upsellFaresPerLeg,
      },
    },
  }),
  [FETCH_FLIGHT_CALENDAR]: (state, action) => ({
    flightCalendar: {
      ...state.flightCalendar,
      [action.key]: action.data,
    },
    flightCalendarLoading: {
      ...state.flightCalendarLoading,
      [action.key]: false,
    },
  }),
  [FETCH_POPULAR_AIRPORTS]: (state, action) => ({
    popularAirports: action.data,
  }),
  [FETCH_FLIGHT_CREDIT_RESERVATION]: (state, action) => ({
    creditRebookingReservation: action.data,
    isCreditReservationFetching: false,
    isCreditReservationError: false,
  }),
  [FETCH_FLIGHT_CAMPAIGNS]: (state, action) => ({
    campaigns: {
      ...state.campaigns,
      [action.key]: {
        data: action.data.rows,
        error: undefined,
        fetching: false,
        isEnabled: action.data.isEnabled,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGN]: (state, action: ApiAction<App.FlightCampaign>) => ({
    campaign: {
      ...state.campaign,
      [action.key]: {
        data: action.data,
        error: undefined,
        fetching: false,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGN_DEALS]: (state, action: ApiAction<Array<App.FlightDeal>>) => ({
    campaignDeals: {
      ...state.campaignDeals,
      [action.key]: {
        data: action.data,
        error: undefined,
        fetching: false,
      },
    },
  }),
  [FETCH_FLIGHT_DEALS]: (state, action: ApiAction<Array<App.FlightDeal>>) => ({
    flightDeals: {
      ...state.flightDeals,
      ...arrayToObject(
        action.data,
        deal => deal.id,
        // if we already have it, don't overwrite - the singular endpoint returns more data at the moment
        deal => ({
          error: undefined,
          fetching: false,
          deal,
        }),
      ),
    },
    flightDealLists: {
      ...state.flightDealLists,
      [action.key]: {
        dealIds: action.data?.map(deal => deal.id) ?? [],
        error: undefined,
        fetching: false,
      },
    },
  }),
  [FETCH_FLIGHT_DEAL]: (state, action) => ({
    flightDeals: {
      ...state.flightDeals,
      [action.id]: {
        error: undefined,
        fetching: false,
        deal: action.data,
      },
    },
  }),
  [UPDATE_FLIGHT_TRAVELLERS]: () => ({
    isUpdatingTravellers: false,
    hasUpdatedTravellers: true,
  }),
  [FETCH_FLIGHT_SEATS]: (state, action) => ({
    seatsMap: {
      ...state.seatsMap,
      [action.key]: {
        fetching: false,
        data: action.data,
      },
    },
  }),
})

const apiFailures = reducerSwitch<App.FlightsState>({
  [FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST]: (state, action) => ({
    flightsDateChangeSearch: {
      ...state.flightsDateChangeSearch,
      [action.key]: {
        fares: [],
        fareIdsToUpsellFarePerLeg: {},
        isFetching: false,
        error: action.error,
      },
    },
  }),
  [FETCH_AIRLINES]: (state, action) => ({
    airlines: { ...state.airlines, [action.key]: { data: undefined, fetching: false } },
  }),
  [FETCH_FLIGHT_JOURNEY]: (state, action) => ({
    journeyErrors: { ...state.journeyErrors, [action.key]: action.error.message },
    journeyFetching: { ...state.journeyFetching, [action.key]: false },
  }),
  [FETCH_FLIGHTS]: (state, action) => ({
    hasError: true,
    isFetching: false,
    errorMessage: action.error.message,
  }),
  [FETCH_FLIGHT_PRICE]: (state, action) => ({
    flightPricesLoading: {
      ...state.flightPricesLoading,
      [action.key]: false,
    },
  }),
  [FETCH_FLIGHT_FARE_FAMILIES]: (state, action) => ({
    fareFamiliesByFlight: {
      ...state.fareFamiliesByFlight,
      [action.key]: {
        fetching: false,
        error: action.error,
        families: [],
        familiesPerLeg: [],
      },
    },
  }),
  [FETCH_FLIGHT_CALENDAR]: (state, action) => ({
    flightCalendarLoading: {
      ...state.flightCalendarLoading,
      [action.key]: false,
    },
  }),
  [FETCH_SEARCH_FLIGHTS]: (state, action) => ({
    searchFlights: {
      ...state.searchFlights,
      [action.key]: {
        ...state.searchFlights[action.key],
        fetching: false,
        error: action.error,
      },
    },
    searchV2Flights: {
      ...state.searchV2Flights,
      [action.key]: {
        ...state.searchV2Flights[action.key],
        fetching: false,
        error: action.error,
      },
    },
  }),
  [FETCH_FLIGHT_CREDIT_RESERVATION]: () => ({
    isCreditReservationFetching: false,
    isBaggageFetchingError: true,
  }),
  [FETCH_FLIGHT_DEALS]: (state, action) => ({
    flightDealLists: {
      ...state.flightDealLists,
      [action.key]: {
        dealIds: [],
        error: action.error,
        fetching: false,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGNS]: (state, action) => ({
    campaigns: {
      ...state.campaigns,
      [action.key]: {
        data: [],
        error: action.error,
        fetching: false,
        isEnabled: false,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGN]: (state, action) => ({
    campaign: {
      ...state.campaign,
      [action.key]: {
        data: undefined,
        error: action.error,
        fetching: false,
      },
    },
  }),
  [FETCH_FLIGHT_CAMPAIGN_DEALS]: (state, action) => ({
    campaignDeals: {
      ...state.campaignDeals,
      [action.key]: {
        data: [],
        error: action.error,
        fetching: false,
      },
    },
  }),
  [FETCH_FLIGHT_DEAL]: (state, action) => ({
    flightDeals: {
      ...state.flightDeals,
      [action.id]: {
        error: action.error,
        fetching: false,
      },
    },
  }),
  [UPDATE_FLIGHT_TRAVELLERS]: () => ({
    isUpdatingTravellers: false,
    hasUpdatedTravellers: true,
  }),
  [FETCH_FLIGHT_SEATS]: (state, action) => ({
    seatsMap: {
      ...state.seatsMap,
      [action.key]: {
        fetching: false,
        data: undefined,
        error: action.error,
      },
    },
  }),
})

const flightsReducer = createReducer<App.FlightsState>(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequests(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailures(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccesses(action.api)(state, action),
  [CLEAR_FLIGHT_RESULT]: () => ({
    journeys: [],
    filters: [],
    flightSearchV2: {
      onewayFares: [],
      searchId: undefined,
    },
    sort: 'fastest',
    isFetching: false,
    isFetched: false,
    hasError: false,
    errorMessage: '',
    isBaggageFetching: false,
    isBaggageFetchingError: false,
  }),
  [CLEAR_FLIGHT_SEARCH_RESULT]: () => ({
    searchV2Flights: {},
    searchFlights: {},
    fareFamiliesByFlight: {},
  }),
  [UPDATE_FLIGHT_FILTERS]: (state, action) => ({
    filters: sortBy(action.data, (f: App.FlightFilter<App.AnyJourney>) => f.order, 'asc'),
  }),
  [UPDATE_FLIGHTS_SORT]: (state, action) => ({
    sort: action.data,
  }),
  [SET_FLIGHTS_CREDIT_DATA]: (state, action) => ({
    creditDetails: action.data,
  }),
  [UPDATE_FLIGHT_SEAT_SELECTIONS]: (state, action) => ({
    ...state,
    seatSelections: {
      ...state.seatSelections,
      [action.key]: action.seatSelections,
    },
  }),
  [CLEAR_FLIGHT_SEAT_SELECTIONS]: (state) => ({
    ...state,
    seatSelections: {},
    seatsMap: {},
  }),
  [FLIGHTS_DATE_CHANGE_SET_STEP]: (state, action) => ({
    flightsDateChange: {
      ...state.flightsDateChange,
      currentStep: action.step,
    },
  }),
  [FLIGHTS_DATE_CHANGE_SELECTED_LEGS_TO_CHANGE]: (state, action) => ({
    flightsDateChange: {
      ...state.flightsDateChange,
      selectedLegsToChange: action.selectedLegsToChange,
    },
  }),
  [FLIGHTS_DATE_CHANGE_NEW_LEGS_WITH_DATES]: (state, action) => ({
    flightsDateChange: {
      ...state.flightsDateChange,
      selectedLegWithNewDepartureDate: action.selectedLegsWithDates,
    },
  }),
  [FLIGHTS_DATE_CHANGE_RESET]: () => ({
    flightsDateChange: EMPTY_FLIGHTS_DATE_CHANGE,
  }),
  // Entering or completing a checkout starts the date-change flow over, so a step or
  // selection left behind by an abandoned attempt cannot be picked up by the next one.
  [CHECKOUT_INITIALISE]: () => ({
    flightsDateChange: EMPTY_FLIGHTS_DATE_CHANGE,
    flightsDateChangeSearch: {},
  }),
  [CHECKOUT_PROCESS_SUCCESS]: () => ({
    flightsDateChange: EMPTY_FLIGHTS_DATE_CHANGE,
    flightsDateChangeSearch: {},
  }),
})

export default flightsReducer
